/**
 * SUPPORT-ONLY. Controller for the OCPP timelog tool.
 *
 * Resolves one of four lookups into a charger device id + time window, then
 * fetches that charger's OCPP logs and builds the timeline plus any overlays:
 *  - charger    : load logs directly over the chosen window.
 *  - receipt    : scrape the receipt -> device id + session window + billing
 *                 overlay (the focus highlight + connector/transaction scope).
 *  - transaction: look up the transaction -> its receipt id -> receipt path
 *                 (falls back to the transaction's own window if no receipt).
 *  - session    : look up the session -> device id + window only (no overlay).
 *
 * Every resolved view is mirrored into the URL (see urlState) so links reproduce
 * it. Existing repo APIs are used for transactions/sessions; only the receipt
 * fetch is bespoke (and clearly support-only).
 */
import {
  buildTimeline,
  fromChargeLabApi,
  type FocusWindow,
  type InitialScope,
  type OcppTimeline,
  type TimelineBand,
} from '@chargelab/ocpp-log-visualizer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useLazyFetchTransactionByIdQuery } from '../../../services/history.api';
import { useLazyFetchOcppMessagesByChargerQuery } from '../../../services/ocpp-messages.api';
import { useLazyFetchSessionByIdQuery } from '../../../services/session.api';
import { getReceiptWebAppUrlPrefix } from '../../../stores/selectors/deployment.selector';
import { Session } from '../../../stores/types';
import { buildBillingBand } from './receipt/billingBand';
import { fetchReceipt, ReceiptCorsError } from './receipt/fetchReceipt';
import { ReceiptInfo, ReceiptSummary } from './receipt/receiptParser';
import { TimelogState, DEFAULT_PADDING_MINUTES, MESSAGE_LIMIT } from './types';
import { decodeState, mergeStateIntoParams } from './urlState';

/** Charger device id off a transaction/session's port (port.charger is loosely typed). */
const chargerIdFromPort = (port?: { id?: string; charger?: { id?: string } }): string | undefined =>
  port?.charger?.id;

/** Extract a human-readable message from an Error or an RTK Query FetchBaseQueryError. */
const extractErrorMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    if ('status' in obj) {
      // RTK Query FetchBaseQueryError shapes:
      //   network-level: { status: 'FETCH_ERROR'|..., error: string }
      //   HTTP error:    { status: number, data: unknown }
      if (typeof obj.error === 'string') return obj.error;
      const { data, status } = obj;
      if (data && typeof data === 'object') {
        const msg = (data as Record<string, unknown>).message;
        if (typeof msg === 'string') return msg;
      }
      if (typeof data === 'string' && data) return data;
      return `HTTP ${status}`;
    }
    if (typeof obj.message === 'string') return obj.message;
  }
  return String(e);
};

export type TimelogStatus = 'idle' | 'resolving' | 'loadingLogs' | 'ready' | 'empty' | 'error';

const connectorFromPortId = (portId?: string): number | undefined => {
  const n = Number(portId?.split('.').pop());
  return Number.isFinite(n) ? n : undefined;
};

const parseDate = (iso?: string | Date): Date | undefined => {
  if (!iso) return undefined;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

const paddedWindow = (
  start: Date,
  end: Date,
  beforeMinutes: number,
  afterMinutes: number,
): { from: string; to: string } => ({
  from: new Date(start.getTime() - beforeMinutes * 60_000).toISOString(),
  to: new Date(end.getTime() + afterMinutes * 60_000).toISOString(),
});

export interface UseOcppTimelog {
  status: TimelogStatus;
  error: string | null;
  warning: string | null;
  applied: TimelogState | null;
  timeline: OcppTimeline | null;
  receiptSummary: ReceiptSummary | null;
  receiptInfo: ReceiptInfo | null;
  transactionData: Session | null;
  extraBands: TimelineBand[] | undefined;
  initialFocusWindow: FocusWindow | undefined;
  initialViewport: { start: Date; end: Date } | undefined;
  initialScope: InitialScope | undefined;
  /** Bump on each load so the Explorer re-seeds its initial props. */
  resetKey: number;
  loadCharger: (
    chargerId: string,
    from: Date,
    to: Date,
    fromSpec?: string,
    toSpec?: string,
  ) => Promise<void>;
  loadReceipt: (receiptId: string, paddingBefore: number, paddingAfter: number) => Promise<void>;
  loadTransaction: (txnId: string, paddingBefore: number, paddingAfter: number) => Promise<void>;
  loadSession: (sessionId: string, paddingBefore: number, paddingAfter: number) => Promise<void>;
}

export const useOcppTimelog = (): UseOcppTimelog => {
  const webAppUrlPrefix = useSelector(getReceiptWebAppUrlPrefix);
  const [, setSearchParams] = useSearchParams();

  const [fetchMessages] = useLazyFetchOcppMessagesByChargerQuery();
  const [fetchTransactionById] = useLazyFetchTransactionByIdQuery();
  const [fetchSessionById] = useLazyFetchSessionByIdQuery();

  const [applied, setApplied] = useState<TimelogState | null>(null);
  const [timeline, setTimeline] = useState<OcppTimeline | null>(null);
  const [receiptInfo, setReceiptInfo] = useState<ReceiptInfo | null>(null);
  const [transactionData, setTransactionData] = useState<Session | null>(null);
  const [status, setStatus] = useState<TimelogStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);

  /** Load logs (and any receipt overlay) for a fully-resolved state, sync URL. */
  const runApplied = useCallback(
    async (state: TimelogState, preInfo?: ReceiptInfo | null) => {
      setApplied(state);
      // Merge into the existing query string so route-critical params (e.g.
      // companyId, which the support guard requires) are preserved.
      setSearchParams((prev) => mergeStateIntoParams(prev, state), { replace: true });
      setError(null);
      setWarning(null);
      setTimeline(null);
      setReceiptInfo(null);
      setStatus('loadingLogs');
      try {
        // Best-effort billing overlay — a CORS block must not stop the logs.
        let info: ReceiptInfo | null = preInfo ?? null;
        if (state.receiptId && !info) {
          try {
            info = await fetchReceipt(state.receiptId, webAppUrlPrefix);
          } catch (e) {
            setWarning(
              e instanceof ReceiptCorsError
                ? `Billing overlay unavailable — ${e.message}`
                : `Receipt ${state.receiptId} could not be loaded for the billing overlay: ${
                    e instanceof Error ? e.message : String(e)
                  }`,
            );
          }
        }
        setReceiptInfo(info);

        const res = (await fetchMessages({
          chargerId: state.chargerId,
          from: state.from,
          to: state.to,
          limit: MESSAGE_LIMIT,
        }).unwrap()) as {
          totalCount: number;
          entities: { receivedTimeUtc: string; messageType: string; message: string }[];
        };

        if (!res?.entities?.length) {
          setStatus('empty');
          setResetKey((k) => k + 1);
          return;
        }

        setTimeline(
          buildTimeline(
            fromChargeLabApi({
              totalCount: res.totalCount,
              entities: res.entities.map((e) => ({
                deviceId: state.chargerId,
                receivedTimeUtc: e.receivedTimeUtc,
                messageType: e.messageType,
                chargerName: state.chargerId,
                message: e.message,
              })),
            }),
          ),
        );
        setStatus('ready');
        setResetKey((k) => k + 1);
      } catch (e) {
        setError(extractErrorMessage(e));
        setStatus('error');
      }
    },
    [fetchMessages, setSearchParams, webAppUrlPrefix],
  );

  const loadCharger = useCallback(
    async (chargerId: string, from: Date, to: Date, fromSpec?: string, toSpec?: string) => {
      const id = chargerId.trim();
      if (!id) return;
      setTransactionData(null);
      await runApplied({
        mode: 'charger',
        chargerId: id,
        from: from.toISOString(),
        to: to.toISOString(),
        fromSpec,
        toSpec,
      });
    },
    [runApplied],
  );

  /** Build the resolved state from a parsed receipt (shared by receipt + transaction). */
  const stateFromReceipt = useCallback(
    (
      info: ReceiptInfo,
      mode: TimelogState['mode'],
      paddingBefore: number,
      paddingAfter: number,
      extra: Partial<TimelogState> = {},
    ): TimelogState => {
      const { from, to } = paddedWindow(
        info.sessionStart,
        info.sessionEnd,
        paddingBefore,
        paddingAfter,
      );
      const conn = Number(info.summary.connectorId);
      const tx = Number(info.summary.transactionId);
      return {
        mode,
        chargerId: info.deviceId,
        from,
        to,
        receiptId: info.receiptId,
        focusFrom: info.sessionStart.toISOString(),
        focusTo: info.sessionEnd.toISOString(),
        connector: Number.isFinite(conn) ? conn : undefined,
        ocppTxId: Number.isFinite(tx) ? tx : undefined,
        ...extra,
      };
    },
    [],
  );

  const loadReceipt = useCallback(
    async (receiptId: string, paddingBefore: number, paddingAfter: number) => {
      const id = receiptId.trim();
      if (!id) return;
      setStatus('resolving');
      setError(null);
      setTransactionData(null);
      try {
        const info = await fetchReceipt(id, webAppUrlPrefix);
        await runApplied(stateFromReceipt(info, 'receipt', paddingBefore, paddingAfter), info);
        // Best-effort: fetch the linked transaction for user email and other fields.
        if (info.summary.transactionId) {
          try {
            const tx = await fetchTransactionById(info.summary.transactionId).unwrap();
            setTransactionData(tx);
          } catch {
            // Non-fatal — session details card just won't show user email.
          }
        }
      } catch (e) {
        setError(extractErrorMessage(e));
        setStatus('error');
      }
    },
    [fetchTransactionById, runApplied, stateFromReceipt, webAppUrlPrefix],
  );

  const loadTransaction = useCallback(
    async (txnId: string, paddingBefore: number, paddingAfter: number) => {
      const id = txnId.trim();
      if (!id) return;
      setStatus('resolving');
      setError(null);
      setTransactionData(null);
      try {
        const tx: Session = await fetchTransactionById(id).unwrap();
        // Preferred path: transaction -> receipt -> overlay + window + device.
        if (tx.receiptId) {
          try {
            const info = await fetchReceipt(tx.receiptId, webAppUrlPrefix);
            await runApplied(
              stateFromReceipt(info, 'transaction', paddingBefore, paddingAfter, {
                txnId: id,
              }),
              info,
            );
            setTransactionData(tx);
            return;
          } catch {
            // Receipt unavailable (e.g. CORS) — fall through to the tx's own window.
          }
        }
        // Fallback: use the transaction's own device + window.
        const chargerId = chargerIdFromPort(tx.port) ?? tx.chargerId;
        const start = parseDate(tx.startTime) ?? parseDate(tx.createTime);
        const end = parseDate(tx.stopTime) ?? parseDate(tx.completeTime) ?? new Date();
        if (!chargerId || !start) {
          throw new Error(`transaction "${id}" has no usable charger id or start time`);
        }
        const { from, to } = paddedWindow(start, end, paddingBefore, paddingAfter);
        await runApplied({
          mode: 'transaction',
          txnId: id,
          chargerId,
          from,
          to,
          receiptId: tx.receiptId,
          focusFrom: start.toISOString(),
          focusTo: end.toISOString(),
          connector: connectorFromPortId(tx.port?.id ?? tx.portId),
        });
        setTransactionData(tx);
      } catch (e) {
        setError(extractErrorMessage(e));
        setStatus('error');
      }
    },
    [fetchTransactionById, runApplied, stateFromReceipt, webAppUrlPrefix],
  );

  const loadSession = useCallback(
    async (sessionId: string, paddingBefore: number, paddingAfter: number) => {
      const id = sessionId.trim();
      if (!id) return;
      setStatus('resolving');
      setError(null);
      setTransactionData(null);
      try {
        const s: Session = await fetchSessionById(id).unwrap();
        const chargerId = chargerIdFromPort(s.port) ?? s.chargerId;
        const start = parseDate(s.createTime) ?? parseDate(s.startTime);
        const end = parseDate(s.completeTime) ?? parseDate(s.stopTime) ?? new Date();
        if (!chargerId || !start) {
          throw new Error(`session "${id}" has no usable charger id or start time`);
        }
        const { from, to } = paddedWindow(start, end, paddingBefore, paddingAfter);
        await runApplied({
          mode: 'session',
          sessionId: id,
          chargerId,
          from,
          to,
          focusFrom: start.toISOString(),
          focusTo: end.toISOString(),
          connector: connectorFromPortId(s.port?.id ?? s.portId),
        });
      } catch (e) {
        setError(extractErrorMessage(e));
        setStatus('error');
      }
    },
    [fetchSessionById, runApplied],
  );

  // ---- reconstruct from a shared URL, once on mount ----
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const decoded = decodeState(new URLSearchParams(window.location.search));
    const p = DEFAULT_PADDING_MINUTES;
    if (decoded.mode === 'receipt' && decoded.receiptId) {
      void loadReceipt(decoded.receiptId, p, p);
    } else if (decoded.mode === 'transaction' && decoded.txnId) {
      void loadTransaction(decoded.txnId, p, p);
    } else if (decoded.mode === 'session' && decoded.sessionId) {
      void loadSession(decoded.sessionId, p, p);
    } else if (decoded.mode === 'charger' && decoded.chargerId && decoded.from && decoded.to) {
      void runApplied({
        mode: 'charger',
        chargerId: decoded.chargerId,
        from: decoded.from,
        to: decoded.to,
        focusFrom: decoded.focusFrom,
        focusTo: decoded.focusTo,
        connector: decoded.connector,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- derived overlays ----
  const extraBands = useMemo<TimelineBand[] | undefined>(() => {
    if (!receiptInfo) return undefined;
    const band = buildBillingBand(receiptInfo.summary, {
      start: receiptInfo.sessionStart,
      end: receiptInfo.sessionEnd,
    });
    return band ? [band] : undefined;
  }, [receiptInfo]);

  const initialFocusWindow = useMemo<FocusWindow | undefined>(() => {
    const start = parseDate(applied?.focusFrom);
    const end = parseDate(applied?.focusTo);
    if (!start || !end) return undefined;
    const label =
      applied?.mode === 'session'
        ? 'session window'
        : applied?.mode === 'transaction'
        ? 'transaction window'
        : 'session';
    return { start, end, label };
  }, [applied]);

  const initialViewport = useMemo(() => {
    const start = parseDate(applied?.from);
    const end = parseDate(applied?.to);
    return start && end ? { start, end } : undefined;
  }, [applied]);

  const initialScope = useMemo<InitialScope | undefined>(() => {
    if (applied?.connector == null && applied?.ocppTxId == null) return undefined;
    return {
      connectors: applied?.connector != null ? [applied.connector] : undefined,
      transactionIds: applied?.ocppTxId != null ? [applied.ocppTxId] : undefined,
    };
  }, [applied]);

  return {
    status,
    error,
    warning,
    applied,
    timeline,
    receiptSummary: receiptInfo?.summary ?? null,
    receiptInfo,
    transactionData,
    extraBands,
    initialFocusWindow,
    initialViewport,
    initialScope,
    resetKey,
    loadCharger,
    loadReceipt,
    loadTransaction,
    loadSession,
  };
};
